import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Calendar, 
  Clock, 
  Users, 
  Plus, 
  Edit2, 
  Trash2, 
  Copy,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Search,
  Filter,
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import './ShiftSchedulerTab.css';

const RAW_API_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const API_BASE_URL = RAW_API_URL.replace(/\/+$/, '').endsWith('/api')
  ? RAW_API_URL.replace(/\/+$/, '')
  : `${RAW_API_URL.replace(/\/+$/, '')}/api`;

const ShiftSchedulerTab = ({ hotelId, token }) => {
  const resolvedToken = (
    token ||
    localStorage.getItem('authToken') ||
    sessionStorage.getItem('authToken') ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('token')
  );

  const sanitizedToken = typeof resolvedToken === 'string'
    ? resolvedToken.replace(/^"+|"+$/g, '').trim()
    : '';

  const authHeaderValue = sanitizedToken
    ? (sanitizedToken.startsWith('Bearer ') ? sanitizedToken : `Bearer ${sanitizedToken}`)
    : null;

  const authConfig = authHeaderValue
    ? { headers: { Authorization: authHeaderValue } }
    : {};

  // View state
  const [activeView, setActiveView] = useState('calendar'); // calendar, templates, assignments, swaps
  
  // Data state
  const [shifts, setShifts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [swapRequests, setSwapRequests] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [staffList, setStaffList] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal state
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingAssignment, setEditingAssignment] = useState(null);
  
  // Form state
  const [templateFormData, setTemplateFormData] = useState({
    name: '',
    start_time: '',
    end_time: '',
    description: ''
  });
  
  const [assignmentFormData, setAssignmentFormData] = useState({
    staff_id: '',
    shift_template_id: '',
    date: '',
    department_id: ''
  });

  // Fetch departments
  useEffect(() => {
    if (hotelId && authHeaderValue) {
      fetchDepartments();
    }
  }, [hotelId, token]);

  // Fetch data based on active view
  useEffect(() => {
    if (hotelId && authHeaderValue) {
      if (activeView === 'calendar') {
        fetchShiftCalendar();
      } else if (activeView === 'templates') {
        fetchTemplates();
      } else if (activeView === 'assignments') {
        fetchAssignments();
      } else if (activeView === 'swaps') {
        fetchSwapRequests();
      }
    }
  }, [activeView, hotelId, token, selectedDate, selectedDepartment]);

  // API Calls
  const fetchDepartments = async () => {
    if (!authHeaderValue) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/departments`, authConfig);
      setDepartments(response.data?.data || response.data || []);
    } catch (err) {
      console.error('Failed to fetch departments:', err);
    }
  };

  const fetchShiftCalendar = async () => {
    if (!authHeaderValue) return;
    setLoading(true);
    setError(null);
    try {
      const startDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)
        .toISOString()
        .split('T')[0];
      const endDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0)
        .toISOString()
        .split('T')[0];
      
      const url = selectedDepartment === 'all'
        ? `${API_BASE_URL}/shifts/hotel/${hotelId}/schedule?start_date=${startDate}&end_date=${endDate}`
        : `${API_BASE_URL}/shifts/hotel/${hotelId}/schedule?start_date=${startDate}&end_date=${endDate}&department_id=${selectedDepartment}`;
      
      const response = await axios.get(url, authConfig);
      setShifts(response.data?.data || response.data || []);
    } catch (err) {
      setError('Failed to load shift calendar');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    if (!authHeaderValue) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/shifts/hotel/${hotelId}/templates`, authConfig);
      const rows = response.data?.data || response.data || [];
      setTemplates(rows.map((template) => ({
        id: template.shift_template_id ?? template.id,
        name: template.shift_name ?? template.name,
        start_time: template.start_time,
        end_time: template.end_time,
        description: template.description,
        assignment_count: template.assignment_count || 0
      })));
    } catch (err) {
      setError('Failed to load shift templates');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async () => {
    if (!authHeaderValue) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/shifts/hotel/${hotelId}/schedule`, authConfig);
      const rows = response.data?.data || response.data || [];
      setAssignments(rows.map((assignment) => ({
        id: assignment.shift_id ?? assignment.id,
        date: assignment.shift_date ?? assignment.date,
        staff_name: assignment.staff_name,
        department_name: assignment.department_name,
        department_id: assignment.department_id,
        shift_name: assignment.shift_name,
        start_time: assignment.start_time,
        end_time: assignment.end_time
      })));
      
      // Fetch staff list for assignment form
      const staffResponse = await axios.get(`${API_BASE_URL}/manager/staff`, authConfig);
      const rawStaff = staffResponse.data?.data?.staff || staffResponse.data?.data || staffResponse.data || [];
      setStaffList(rawStaff.map((staff) => ({
        id: staff.staff_profile_id ?? staff.id,
        name: staff.full_name || staff.name || staff.staff_name,
        department_name: staff.department_name || 'N/A'
      })));
    } catch (err) {
      setError('Failed to load assignments');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSwapRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      setSwapRequests([]);
    } catch (err) {
      setError('Failed to load swap requests');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Template CRUD operations
  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const normalizedName = (templateFormData.name || '').trim();
      const timeCode = (templateFormData.start_time || '').replace(':', '');
      const generatedCode = `${normalizedName.replace(/\s+/g, '').toUpperCase().slice(0, 6) || 'SHIFT'}${timeCode}`;

      await axios.post(
        `${API_BASE_URL}/shifts/hotel/${hotelId}/templates`,
        {
          shift_name: normalizedName,
          shift_code: generatedCode,
          shift_type: 'rotating',
          start_time: templateFormData.start_time,
          end_time: templateFormData.end_time,
          description: templateFormData.description
        },
        authConfig
      );
      
      setShowTemplateModal(false);
      setTemplateFormData({ name: '', start_time: '', end_time: '', description: '' });
      fetchTemplates();
    } catch (err) {
      setError('Failed to create template');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTemplate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await axios.put(
        `${API_BASE_URL}/shifts/templates/${editingTemplate.id}`,
        {
          shift_name: templateFormData.name,
          start_time: templateFormData.start_time,
          end_time: templateFormData.end_time,
          description: templateFormData.description
        },
        authConfig
      );
      
      setShowTemplateModal(false);
      setEditingTemplate(null);
      setTemplateFormData({ name: '', start_time: '', end_time: '', description: '' });
      fetchTemplates();
    } catch (err) {
      setError('Failed to update template');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await axios.delete(`${API_BASE_URL}/shifts/templates/${templateId}`, {
        ...authConfig
      });
      fetchTemplates();
    } catch (err) {
      setError('Failed to delete template');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Assignment operations
  const handleAssignShift = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(
        `${API_BASE_URL}/shifts/assign`,
        {
          staff_profile_id: assignmentFormData.staff_id,
          shift_template_id: assignmentFormData.shift_template_id,
          shift_date: assignmentFormData.date,
          notes: null
        },
        authConfig
      );
      
      setShowAssignmentModal(false);
      setAssignmentFormData({ staff_id: '', shift_template_id: '', date: '', department_id: '' });
      fetchAssignments();
    } catch (err) {
      setError('Failed to assign shift');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to remove this assignment?')) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await axios.delete(`${API_BASE_URL}/shifts/${assignmentId}`, authConfig);
      fetchAssignments();
    } catch (err) {
      setError('Failed to delete assignment');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Swap request operations
  const handleProcessSwap = async (swapId, action) => {
    setLoading(true);
    setError(null);
    
    try {
      await axios.put(
        `${API_BASE_URL}/shifts/${swapId}/process-swap`,
        { action },
        authConfig
      );
      fetchSwapRequests();
    } catch (err) {
      setError(`Failed to ${action} swap request`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const formatTime = (time) => {
    if (!time) return '';
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getShiftsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return shifts.filter(shift => shift.date?.startsWith(dateStr));
  };

  const changeMonth = (delta) => {
    setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + delta, 1));
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateFormData({
      name: template.name,
      start_time: template.start_time,
      end_time: template.end_time,
      description: template.description || ''
    });
    setShowTemplateModal(true);
  };

  const openCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateFormData({ name: '', start_time: '', end_time: '', description: '' });
    setShowTemplateModal(true);
  };

  const openAssignmentModal = () => {
    setAssignmentFormData({ 
      staff_id: '', 
      shift_template_id: '', 
      date: new Date().toISOString().split('T')[0],
      department_id: selectedDepartment === 'all' ? '' : selectedDepartment
    });
    setShowAssignmentModal(true);
  };

  const filteredTemplates = templates.filter(template =>
    template.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredAssignments = assignments.filter(assignment => {
    const matchesSearch = assignment.staff_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         assignment.shift_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDept = selectedDepartment === 'all' || assignment.department_id === parseInt(selectedDepartment);
    return matchesSearch && matchesDept;
  });

  return (
    <div className="shift-scheduler-tab">
      {/* Header */}
      <div className="scheduler-header">
        <div className="header-left">
          <h2>
            <Calendar size={24} />
            Shift Scheduler
          </h2>
          <p>Manage shifts, templates, and assignments</p>
        </div>
        <div className="header-actions">
          <button className="btn-icon" onClick={() => {
            if (activeView === 'calendar') fetchShiftCalendar();
            else if (activeView === 'templates') fetchTemplates();
            else if (activeView === 'assignments') fetchAssignments();
            else if (activeView === 'swaps') fetchSwapRequests();
          }}>
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* View Switcher */}
      <div className="view-switcher">
        <button
          className={activeView === 'calendar' ? 'active' : ''}
          onClick={() => setActiveView('calendar')}
        >
          <Calendar size={18} />
          Calendar
        </button>
        <button
          className={activeView === 'templates' ? 'active' : ''}
          onClick={() => setActiveView('templates')}
        >
          <Clock size={18} />
          Templates
        </button>
        <button
          className={activeView === 'assignments' ? 'active' : ''}
          onClick={() => setActiveView('assignments')}
        >
          <Users size={18} />
          Assignments
        </button>
        <button
          className={activeView === 'swaps' ? 'active' : ''}
          onClick={() => setActiveView('swaps')}
        >
          <RefreshCw size={18} />
          Swap Requests
          {swapRequests.filter(s => s.status === 'pending').length > 0 && (
            <span className="badge">{swapRequests.filter(s => s.status === 'pending').length}</span>
          )}
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-banner">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => setError(null)}><XCircle size={16} /></button>
        </div>
      )}

      {/* Calendar View */}
      {activeView === 'calendar' && (
        <div className="calendar-view">
          <div className="calendar-controls">
            <div className="month-navigator">
              <button onClick={() => changeMonth(-1)}>
                <ChevronLeft size={20} />
              </button>
              <h3>
                {selectedDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
              </h3>
              <button onClick={() => changeMonth(1)}>
                <ChevronRight size={20} />
              </button>
            </div>
            
            <select 
              value={selectedDepartment} 
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="department-filter"
            >
              <option value="all">All Departments</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="loading-state">
              <RefreshCw className="spin" size={32} />
              <p>Loading calendar...</p>
            </div>
          ) : (
            <div className="calendar-grid">
              <div className="calendar-header">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="day-header">{day}</div>
                ))}
              </div>
              <div className="calendar-body">
                {getDaysInMonth(selectedDate).map((day, index) => {
                  const dayShifts = day ? getShiftsForDay(day) : [];
                  const isToday = day && 
                    new Date().getDate() === day && 
                    new Date().getMonth() === selectedDate.getMonth() &&
                    new Date().getFullYear() === selectedDate.getFullYear();
                  
                  return (
                    <div 
                      key={index} 
                      className={`calendar-day ${!day ? 'empty' : ''} ${isToday ? 'today' : ''}`}
                    >
                      {day && (
                        <>
                          <div className="day-number">{day}</div>
                          <div className="day-shifts">
                            {dayShifts.slice(0, 3).map(shift => (
                              <div key={shift.id} className="shift-item" title={`${shift.staff_name} - ${shift.shift_name}`}>
                                <Clock size={12} />
                                <span>{shift.staff_name}</span>
                              </div>
                            ))}
                            {dayShifts.length > 3 && (
                              <div className="shift-more">+{dayShifts.length - 3} more</div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Templates View */}
      {activeView === 'templates' && (
        <div className="templates-view">
          <div className="view-header">
            <div className="search-box">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button className="btn-primary" onClick={openCreateTemplate}>
              <Plus size={18} />
              Create Template
            </button>
          </div>

          {loading ? (
            <div className="loading-state">
              <RefreshCw className="spin" size={32} />
              <p>Loading templates...</p>
            </div>
          ) : (
            <div className="templates-grid">
              {filteredTemplates.map(template => (
                <div key={template.id} className="template-card">
                  <div className="template-header">
                    <div className="template-icon">
                      <Clock size={24} />
                    </div>
                    <div className="template-actions">
                      <button onClick={() => openEditTemplate(template)} title="Edit">
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => handleDeleteTemplate(template.id)} title="Delete">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <h3>{template.name}</h3>
                  <div className="template-time">
                    <span>{formatTime(template.start_time)}</span>
                    <span>→</span>
                    <span>{formatTime(template.end_time)}</span>
                  </div>
                  {template.description && (
                    <p className="template-description">{template.description}</p>
                  )}
                  <div className="template-stats">
                    <span>{template.assignment_count || 0} assignments</span>
                  </div>
                </div>
              ))}
              {filteredTemplates.length === 0 && (
                <div className="empty-state">
                  <Clock size={48} />
                  <h3>No templates found</h3>
                  <p>Create your first shift template to get started</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Assignments View */}
      {activeView === 'assignments' && (
        <div className="assignments-view">
          <div className="view-header">
            <div className="filters-group">
              <div className="search-box">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search assignments..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <select 
                value={selectedDepartment} 
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                <option value="all">All Departments</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </div>
            <button className="btn-primary" onClick={openAssignmentModal}>
              <Plus size={18} />
              Assign Shift
            </button>
          </div>

          {loading ? (
            <div className="loading-state">
              <RefreshCw className="spin" size={32} />
              <p>Loading assignments...</p>
            </div>
          ) : (
            <div className="assignments-list">
              {filteredAssignments.map(assignment => (
                <div key={assignment.id} className="assignment-card">
                  <div className="assignment-info">
                    <div className="staff-avatar">
                      <Users size={20} />
                    </div>
                    <div className="assignment-details">
                      <h4>{assignment.staff_name}</h4>
                      <p className="assignment-meta">
                        <span>{assignment.department_name}</span>
                        <span>•</span>
                        <span>{assignment.shift_name}</span>
                      </p>
                      <p className="assignment-time">
                        <Clock size={14} />
                        {formatTime(assignment.start_time)} - {formatTime(assignment.end_time)}
                      </p>
                    </div>
                  </div>
                  <div className="assignment-date">
                    <Calendar size={14} />
                    <span>{formatDate(assignment.date)}</span>
                  </div>
                  <button 
                    className="btn-icon-danger"
                    onClick={() => handleDeleteAssignment(assignment.id)}
                    title="Remove assignment"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              {filteredAssignments.length === 0 && (
                <div className="empty-state">
                  <Users size={48} />
                  <h3>No assignments found</h3>
                  <p>Assign shifts to staff members to see them here</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Swap Requests View */}
      {activeView === 'swaps' && (
        <div className="swaps-view">
          <div className="view-header">
            <h3>Shift Swap Requests</h3>
          </div>

          {loading ? (
            <div className="loading-state">
              <RefreshCw className="spin" size={32} />
              <p>Loading swap requests...</p>
            </div>
          ) : (
            <div className="swaps-list">
              {swapRequests.map(swap => (
                <div key={swap.id} className={`swap-card status-${swap.status}`}>
                  <div className="swap-header">
                    <div className={`status-badge ${swap.status}`}>
                      {swap.status === 'pending' && <Clock size={14} />}
                      {swap.status === 'approved' && <CheckCircle size={14} />}
                      {swap.status === 'rejected' && <XCircle size={14} />}
                      {swap.status}
                    </div>
                    <span className="swap-date">{formatDate(swap.created_at)}</span>
                  </div>
                  
                  <div className="swap-details">
                    <div className="swap-staff">
                      <div className="staff-info">
                        <Users size={16} />
                        <div>
                          <p className="staff-name">{swap.requester_name}</p>
                          <p className="shift-info">{swap.original_shift_name} - {formatDate(swap.original_date)}</p>
                        </div>
                      </div>
                      <RefreshCw size={20} className="swap-icon" />
                      <div className="staff-info">
                        <Users size={16} />
                        <div>
                          <p className="staff-name">{swap.target_name}</p>
                          <p className="shift-info">{swap.target_shift_name} - {formatDate(swap.target_date)}</p>
                        </div>
                      </div>
                    </div>
                    
                    {swap.reason && (
                      <div className="swap-reason">
                        <strong>Reason:</strong> {swap.reason}
                      </div>
                    )}
                  </div>

                  {swap.status === 'pending' && (
                    <div className="swap-actions">
                      <button 
                        className="btn-success"
                        onClick={() => handleProcessSwap(swap.id, 'approved')}
                      >
                        <CheckCircle size={16} />
                        Approve
                      </button>
                      <button 
                        className="btn-danger"
                        onClick={() => handleProcessSwap(swap.id, 'rejected')}
                      >
                        <XCircle size={16} />
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {swapRequests.length === 0 && (
                <div className="empty-state">
                  <RefreshCw size={48} />
                  <h3>No swap requests</h3>
                  <p>Shift swap requests will appear here</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="modal-overlay" onClick={() => setShowTemplateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingTemplate ? 'Edit Template' : 'Create Template'}</h3>
              <button onClick={() => setShowTemplateModal(false)}>
                <XCircle size={20} />
              </button>
            </div>
            <form onSubmit={editingTemplate ? handleUpdateTemplate : handleCreateTemplate}>
              <div className="form-group">
                <label>Template Name *</label>
                <input
                  type="text"
                  value={templateFormData.name}
                  onChange={(e) => setTemplateFormData({ ...templateFormData, name: e.target.value })}
                  placeholder="e.g., Morning Shift, Night Shift"
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Time *</label>
                  <input
                    type="time"
                    value={templateFormData.start_time}
                    onChange={(e) => setTemplateFormData({ ...templateFormData, start_time: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>End Time *</label>
                  <input
                    type="time"
                    value={templateFormData.end_time}
                    onChange={(e) => setTemplateFormData({ ...templateFormData, end_time: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={templateFormData.description}
                  onChange={(e) => setTemplateFormData({ ...templateFormData, description: e.target.value })}
                  placeholder="Optional description"
                  rows="3"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowTemplateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : editingTemplate ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {showAssignmentModal && (
        <div className="modal-overlay" onClick={() => setShowAssignmentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Assign Shift</h3>
              <button onClick={() => setShowAssignmentModal(false)}>
                <XCircle size={20} />
              </button>
            </div>
            <form onSubmit={handleAssignShift}>
              <div className="form-group">
                <label>Staff Member *</label>
                <select
                  value={assignmentFormData.staff_id}
                  onChange={(e) => setAssignmentFormData({ ...assignmentFormData, staff_id: e.target.value })}
                  required
                >
                  <option value="">Select staff member</option>
                  {staffList.map(staff => (
                    <option key={staff.id} value={staff.id}>
                      {staff.name} - {staff.department_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Shift Template *</label>
                <select
                  value={assignmentFormData.shift_template_id}
                  onChange={(e) => setAssignmentFormData({ ...assignmentFormData, shift_template_id: e.target.value })}
                  required
                >
                  <option value="">Select shift template</option>
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name} ({formatTime(template.start_time)} - {formatTime(template.end_time)})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  value={assignmentFormData.date}
                  onChange={(e) => setAssignmentFormData({ ...assignmentFormData, date: e.target.value })}
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowAssignmentModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Assigning...' : 'Assign Shift'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftSchedulerTab;